import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getUser, getPlan } from '@/utils/localStorage';
import { getAllowedRoutes } from '@/@auth/authRoles';

type ProtectedRouteProps = {
	children: React.ReactNode;
	requireAuth?: boolean;
	allowedRoles?: string[];
};

/**
 * ProtectedRoute component - replaces Next.js middleware
 * Handles authentication and role-based access control
 */
export function ProtectedRoute({
	children,
	requireAuth = true,
	allowedRoles
}: ProtectedRouteProps) {
	const location = useLocation();
	const authenticated = isAuthenticated();
	const user = getUser();
	const plan = getPlan();

	// If auth is required but user is not authenticated, redirect to sign-in
	if (requireAuth && !authenticated) {
		return <Navigate to="/sign-in" state={{ from: location }} replace />;
	}

	// If user is authenticated but trying to access auth pages, redirect to dashboard
	if (!requireAuth && authenticated) {
		return <Navigate to="/dashboard" replace />;
	}

	// Role-based and plan-based access control
	if (authenticated && user) {
		const userRole = user.role;
		// Treat empty object {} as no plan (aligned with plans page logic)
		const hasPlan =
			plan != null &&
			typeof plan === 'object' &&
			Object.keys(plan).length > 0 &&
			plan.id != null &&
			plan.id !== '';

		// If allowedRoles specified, user must be in it
		if (allowedRoles && !allowedRoles.includes(userRole)) {
			return <Navigate to="/dashboard" replace />;
		}

		// Check if current route is allowed for this role + plan (Applicant without plan → only /apps/billing/plans)
		const allowedRoutes = getAllowedRoutes(userRole, hasPlan);
		const isRouteAllowed = allowedRoutes.some(
			(route) => location.pathname === route || location.pathname.startsWith(route + '/')
		);

		if (!isRouteAllowed) {
			if (userRole === 'Applicant' && !hasPlan) {
				return <Navigate to="/apps/billing/plans" replace />;
			}
			return <Navigate to="/dashboard" replace />;
		}
	}

	return <>{children}</>;
}

